import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  TextField,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import { Checkbox, FormControlLabel } from '@mui/material';
import {
  Payment,
  CreditCard,
  AccountBalance,
  QrCode,
  Security,
  Language,
  CheckCircle,
  Error as ErrorIcon,
  Info,
  Warning,
  Close,
  Refresh,
  ContentCopy,
  Launch,
  Shield,
  Verified
} from '@mui/icons-material';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { QRCodeSVG as QRCodeComponent } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/apiConfig';
import { waitForRazorpay } from '../utils/razorpayLoader';
import useFlowLegalDocuments from '../hooks/useFlowLegalDocuments';
import FlowAgreementsSection from './legal/FlowAgreementsSection';
import ConsentModal from './legal/ConsentModal';
import { recordFlowConsent } from '../utils/legalConsent';
import { BILLING_EMAIL, INFO_EMAIL } from '../config/contact';

const EnhancedPayment = ({ planId, planName, onSuccess, onClose }) => {
  const { user, token, refreshPendingConsents } = useAuth();
  const [viewDoc, setViewDoc] = useState(null);
  const {
    documents: paymentDocs,
    checked: paymentChecked,
    onCheckChange: onPaymentCheckChange,
    loading: paymentDocsLoading,
    error: paymentDocsError,
    canProceed: paymentTermsAccepted,
    documentIds: paymentDocumentIds,
  } = useFlowLegalDocuments('payment');

  // State management
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Payment data
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [selectedGateway, setSelectedGateway] = useState('');
  const [paymentIntent, setPaymentIntent] = useState(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState(0);
  const [showUpiQr, setShowUpiQr] = useState(false);
  const [upiData, setUpiData] = useState(null);
  const [countdown, setCountdown] = useState(0);

  // Consent state
  // Stores the resolved plan slug (e.g. "enterprise") so verifyPayment
  // can send it to the backend without an extra round-trip.
  const planSlugRef = useRef(null);
  const paymentErrorRef = useRef(''); // tracks errors set during createOrder so onError doesn't overwrite them
  const [autoRenewConsent, setAutoRenewConsent] = useState(false);
  const consentText = `I agree to auto-renew my ${planName} subscription every ${pricing?.interval || 'billing cycle'} until I cancel. I can cancel anytime in account settings, and recurring charges will be applied to my selected payment method.`;
  const paymentCheckoutReady = paymentTermsAccepted && autoRenewConsent;

  useEffect(() => {
    loadPaymentData();
  }, [planId]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);



  const loadPaymentData = async () => {
    try {
      setLoading(true);
      
      // Fallback payment methods for testing when not authenticated
      const fallbackMethods = [
        {
          gateway: 'paypal',
          name: 'PayPal',
          currencies: ['USD', 'EUR', 'GBP', 'CAD', 'AUD'],
          methods: ['paypal'],
          icon: 'paypal'
        },
        {
          gateway: 'stripe',
          name: 'Credit/Debit Card',
          currencies: ['USD', 'EUR', 'GBP', 'CAD', 'AUD'],
          methods: ['card'],
          icon: 'credit-card'
        },
        {
          gateway: 'razorpay',
          name: 'Razorpay',
          currencies: ['INR'],
          methods: ['card', 'upi', 'netbanking', 'wallet'],
          icon: 'razorpay'
        }
      ];
      
      // Try to load payment methods if authenticated, otherwise use fallback
      if (token) {
        try {
          const methodsResponse = await fetch('/api/payments/methods', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          const methodsData = await methodsResponse.json();
          
          if (methodsData.success && (methodsData.methods || []).length > 0) {
            // Mark recommended gateway in methods for UI hint
            const recommendedGateway = methodsData.recommendedGateway || 'paypal';
            const markedMethods = methodsData.methods.map(m => ({
              ...m,
              recommended: m.gateway === recommendedGateway
            }));
            setPaymentMethods(markedMethods);

            // Set default currency and gateway based on user location
            const userCurrency = methodsData.recommendedCurrency || 'USD';
            const userGateway = recommendedGateway;

            setSelectedCurrency(getValidCurrencyCode(userCurrency));
            setSelectedGateway(userGateway);
            
            // Load pricing for the plan
            await loadPricing(planId, userCurrency);
            return;
          }
        } catch (authError) {
          console.log('Authentication required, using fallback data for testing');
        }
      }
      
      // Use fallback data for testing
      setPaymentMethods(fallbackMethods);
      setSelectedCurrency('USD');
      setSelectedGateway('paypal');
      
      // Set fallback pricing for testing
      setPricing({
        planId: planId || 'premium',
        planName: planName || 'Premium Plan',
        currency: 'USD',
        amount: 29.99,
        interval: 'month',
        features: ['Unlimited interviews', 'AI feedback', 'Resume analysis']
      });
      
    } catch (error) {
      console.error('Error loading payment data:', error);
      setError('Failed to load payment options');
    } finally {
      setLoading(false);
    }
  };

  const loadPricing = async (planId, currency) => {
    try {
      if (!token) {
        // Fallback pricing for testing when not authenticated
        setPricing({
          planId: planId || 'premium',
          planName: planName || 'Premium Plan',
          currency: currency || 'USD',
          amount: currency === 'EUR' ? 24.99 : currency === 'GBP' ? 21.99 : 29.99,
          interval: 'month',
          features: ['Unlimited interviews', 'AI feedback', 'Resume analysis']
        });
        return;
      }
      
      const response = await fetch(`/api/payments/pricing/${planId}?currency=${getValidCurrencyCode(currency)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setPricing(data.pricing);
      }
    } catch (error) {
      console.error('Error loading pricing:', error);
      // Fallback pricing on error
      setPricing({
        planId: planId || 'premium',
        planName: planName || 'Premium Plan',
        currency: currency || 'USD',
        amount: currency === 'EUR' ? 24.99 : currency === 'GBP' ? 21.99 : 29.99,
        interval: 'month',
        features: ['Unlimited interviews', 'AI feedback', 'Resume analysis']
      });
    }
  };

  const handleCurrencyChange = async (currency) => {
    const cleanCurrency = getValidCurrencyCode(currency);
    setSelectedCurrency(cleanCurrency);
    await loadPricing(planId, cleanCurrency);
    
    // Update recommended gateway based on currency
    const method = paymentMethods.find(m => m.currencies.includes(cleanCurrency));
    if (method) {
      setSelectedGateway(method.gateway);
    }
  };

  // Ensure Razorpay uses INR and pricing reflects INR explicitly
  useEffect(() => {
    if (selectedGateway === 'razorpay' && selectedCurrency !== 'INR') {
      setSelectedCurrency('INR');
      loadPricing(planId, 'INR');
    }
  }, [selectedGateway]);

  // Extract valid currency code from potentially malformed currency values
  const getValidCurrencyCode = (currency) => {
    console.log('getValidCurrencyCode input:', currency);
    if (!currency) {
      console.log('getValidCurrencyCode returning USD (no currency)');
      return 'USD';
    }
    
    // If currency contains colons (like USD:3:75885), extract just the currency code
    const currencyCode = currency.split(':')[0];
    console.log('getValidCurrencyCode extracted currency code:', currencyCode);
    
    // Validate it's a 3-letter currency code
    if (/^[A-Z]{3}$/.test(currencyCode)) {
      console.log('getValidCurrencyCode returning valid currency:', currencyCode);
      return currencyCode;
    }
    
    console.log('getValidCurrencyCode returning USD (fallback)');
    return 'USD'; // fallback
  };

  // Convert planId to planType
  const getPlanTypeFromId = async (planId) => {
    try {
      const response = await fetch(`/api/payments/pricing/${planId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.planType.toLowerCase();
      }
    } catch (error) {
      console.error('Error getting plan type:', error);
    }
    
    // Fallback: try to map common plan names to DB slugs
    const planNameLower = planName?.toLowerCase();
    if (planNameLower?.includes('premium') || planNameLower?.includes('pro')) return 'pro';
    if (planNameLower?.includes('enterprise')) return 'enterprise';
    return 'pro'; // default fallback
  };

  const createPaymentIntent = async () => {
    console.log('createPaymentIntent called');
    try {
      setProcessing(true);
      setError('');
      paymentErrorRef.current = ''; // reset on each new attempt

      console.log('Current state:', {
        planId,
        selectedCurrency,
        selectedGateway,
        token: token ? 'present' : 'missing',
        API_URL
      });

      // Convert planId to planType (also used as plan_slug for verify-payment)
      const planType = await getPlanTypeFromId(planId);
      planSlugRef.current = planType; // save for verifyPayment
      console.log('Plan type resolved:', planType);
      
      const requestBody = {
        planType: planType,
        currency: getValidCurrencyCode(selectedCurrency),
        gateway: selectedGateway,
        // For UPI tab we still use the standard checkout modal (not custom QR),
        // so don't send paymentMethod:'upi' — backend would otherwise generate a
        // QR code and we'd never open Razorpay checkout where success@razorpay works.
        paymentMethod: getSelectedPaymentMethod() === 'upi' ? 'card' : getSelectedPaymentMethod()
      };
      console.log('Request body:', requestBody);
      
      const response = await fetch('/api/payments/create-intent', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);
      
      if (data.success) {
        setPaymentIntent(data.paymentIntent);
        
        // Handle UPI payments
        if (selectedGateway === 'razorpay' && getSelectedPaymentMethod() === 'upi') {
          setUpiData(data.upiData);
          setShowUpiQr(true);
          setCountdown(300); // 5 minutes countdown
          startPaymentPolling(data.paymentIntent.id);
        }
        
        return data.paymentIntent;
      } else {
        throw new Error(data.error || 'Failed to create payment intent');
      }
    } catch (error) {
      console.error('Error creating payment intent:', error);
      paymentErrorRef.current = error.message;
      setError(error.message);
      return null;
    } finally {
      setProcessing(false);
    }
  };

  const startPaymentPolling = (intentId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`/api/payments/status/${intentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        
        if (data.success && data.status === 'succeeded') {
          clearInterval(pollInterval);
          setSuccess('Payment successful!');
          setShowUpiQr(false);
          if (onSuccess) onSuccess(data);
        } else if (data.status === 'failed') {
          clearInterval(pollInterval);
          setError('Payment failed. Please try again.');
          setShowUpiQr(false);
        }
      } catch (error) {
        console.error('Error polling payment status:', error);
      }
    }, 3000);

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      if (showUpiQr) {
        setError('Payment timeout. Please try again.');
        setShowUpiQr(false);
      }
    }, 300000);
  };

  const getSelectedPaymentMethod = () => {
    switch (activeTab) {
      case 0: return 'card';
      case 1: return 'upi';
      case 2: return 'netbanking';
      case 3: return 'wallet';
      default: return 'card';
    }
  };

  const handleRazorpayPayment = async () => {
    const intent = await createPaymentIntent();
    if (!intent) return;

    try {
      // Use the shared lazy loader — injects the script only once, no duplicate requests
      await waitForRazorpay();
    } catch (err) {
      setError('Failed to load Razorpay checkout. Please refresh and try again.');
      return;
    }

    const options = {
      key: intent.keyId,
      amount: Math.round(intent.amount * 100), // Razorpay expects paise (smallest unit)
      currency: intent.currency,
      name: 'JobMockAI',
      description: `${planName} Subscription`,
      order_id: intent.id,
      handler: async (response) => {
        await verifyPayment(response, intent);
      },
      prefill: {
        name: user.name,
        email: user.email,
        contact: user.phone
      },
      theme: {
        color: '#0D47A1'
      },
      // Allow all methods — let Razorpay show whatever the account supports.
      // Do NOT restrict to a single method; international cards are blocked on
      // unactivated accounts so keeping all methods open lets users pick UPI /
      // netbanking / domestic cards without hitting the risk-check wall.
      config: {
        display: {
          // Prefer UPI first so Indian users see the easiest option up front
          sequence: ['block.upi', 'block.card', 'block.netbanking', 'block.wallet'],
          preferences: { show_default_blocks: true }
        }
      },
      // If UPI tab was selected, pre-open the UPI block so the user just types success@razorpay
      ...(getSelectedPaymentMethod() === 'upi' && { method: 'upi' })
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (response) => {
      const errDesc = response?.error?.description || 'Payment failed';
      const errReason = response?.error?.reason || '';
      console.error('[Razorpay] Payment failed:', response?.error);
      if (errReason === 'payment_risk_check_failed') {
        setError('Card declined (international cards are restricted). Please use UPI — enter "success@razorpay" as the UPI ID to complete the test payment.');
      } else {
        setError(`Payment failed: ${errDesc}. Tip: use UPI with "success@razorpay" for test payments.`);
      }
    });
    rzp.open();
  };

  const verifyPayment = async (paymentData, intent) => {
    try {
      setProcessing(true);

      // paymentData from Razorpay handler contains:
      //   razorpay_payment_id, razorpay_order_id, razorpay_signature
      // Route these to the dedicated Razorpay verify endpoint which
      // expects exactly those field names (not the generic /api/payments/verify
      // which requires { paymentId, gateway } and would return "Validation failed").
      const isRazorpayResponse =
        paymentData.razorpay_payment_id && paymentData.razorpay_signature;

      const endpoint = isRazorpayResponse
        ? `${API_URL}/api/v1/billing/razorpay/verify-payment`
        : `${API_URL}/api/payments/verify`;

      // intent.amount is in the smallest currency unit (paise); backend expects base unit (rupees)
      const amountInBaseUnit = intent?.amount ? intent.amount / 100 : undefined;
      const intentId = intent?.id ?? intent;

      const body = isRazorpayResponse
        ? {
            razorpay_payment_id: paymentData.razorpay_payment_id,
            razorpay_order_id:   paymentData.razorpay_order_id,
            razorpay_signature:  paymentData.razorpay_signature,
            plan_slug:           planSlugRef.current,  // ← tells backend which plan to activate
            intentId,
            amount:              amountInBaseUnit,      // ← base unit (e.g. rupees)
            currency:            intent?.currency,      // ← e.g. "INR"
            autoRenewConsent,
            consentText,
          }
        : {
            ...paymentData,
            intentId,
            autoRenewConsent,
            consentText,
          };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      const data = await response.json();

      // /api/v1/billing/razorpay/verify-payment returns { verified: true, subscription: {...} }
      // /api/payments/verify returns { success: true }
      if (data.success || data.verified) {
        if (paymentDocumentIds.length) {
          try {
            await recordFlowConsent(paymentDocumentIds, refreshPendingConsents);
          } catch (consentErr) {
            console.warn('Post-payment legal consent recording failed:', consentErr);
          }
        }
        const planLabel = data.subscription?.plan_name || planName;
        setSuccess(`Payment successful! Your ${planLabel} plan is now active.`);
        // Pass the subscription back so parent components (EnhancedPricingPage,
        // PaymentDashboard) can update their state immediately without waiting
        // for the user to manually refresh.
        if (onSuccess) onSuccess({ ...data, success: true, subscription: data.subscription });
      } else {
        setError(data.error || 'Payment verification failed');
        // Fallback: Close dialog even if verification fails since PayPal payment was successful
        if (paymentData.gateway === 'paypal' && onSuccess) {
          console.warn('Backend verification failed but PayPal payment was successful. Closing dialog as fallback.');
          setTimeout(() => {
            onSuccess({ 
              success: true, 
              paymentId: paymentData.paymentId,
              gateway: 'paypal',
              fallback: true,
              message: 'Payment processed by PayPal but verification pending'
            });
          }, 2000); // Give user time to see the error message
        }
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      setError('Payment verification failed');
      
      // Fallback: Close dialog even if verification fails since PayPal payment was successful
      if (paymentData.gateway === 'paypal' && onSuccess) {
        console.warn('Backend verification error but PayPal payment was successful. Closing dialog as fallback.');
        setTimeout(() => {
          onSuccess({ 
            success: true, 
            paymentId: paymentData.paymentId,
            gateway: 'paypal',
            fallback: true,
            message: 'Payment processed by PayPal but verification pending'
          });
        }, 2000); // Give user time to see the error message
      }
    } finally {
      setProcessing(false);
    }
  };

  const copyUpiId = () => {
    if (upiData?.upi_id) {
      navigator.clipboard.writeText(upiData.upi_id);
      setSuccess('UPI ID copied to clipboard');
    }
  };

  // Mobile device detection
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));
  };

  const openUpiApp = (app) => {
    if (upiData?.deep_links?.[app]) {
      if (isMobileDevice()) {
        // On mobile, try to open the app directly
        window.location.href = upiData.deep_links[app];
      } else {
        // On desktop, show a helpful message
        setError(`UPI app links work on mobile devices. Please scan the QR code with your mobile device or copy the UPI ID to make payment.`);
        setTimeout(() => setError(''), 5000);
      }
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getGatewayIcon = (gateway) => {
    switch (gateway) {
      case 'razorpay': return <Payment />;
      case 'paypal': return <AccountBalance />;
      case 'stripe': return <CreditCard />;
      default: return <Payment />;
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'card': return <CreditCard />;
      case 'upi': return <QrCode />;
      case 'netbanking': return <AccountBalance />;
      case 'wallet': return <Payment />;
      default: return <Payment />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Subscribe to {planName}
            </Typography>
            {onClose && (
              <IconButton onClick={onClose}>
                <Close />
              </IconButton>
            )}
          </Box>
          
          {pricing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {formatCurrency(pricing.amount, selectedCurrency)}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                /{pricing.interval}
              </Typography>
              {pricing.discount > 0 && (
                <Chip 
                  label={`${pricing.discount}% OFF`} 
                  color="success" 
                  size="small" 
                />
              )}
              {selectedGateway === 'razorpay' && (
                <Chip label="INR Pricing" color="primary" size="small" />
              )}
            </Box>
          )}

          {/* Currency Selection */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Currency</InputLabel>
            <Select
              value={selectedCurrency}
              onChange={(e) => handleCurrencyChange(e.target.value)}
              label="Currency"
            >
              {paymentMethods.flatMap(method => method.currencies).filter((currency, index, self) => self.indexOf(currency) === index).map(currency => (
                <MenuItem key={currency} value={currency}>
                  {currency}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Sandbox / Pilot mode banner */}
      {process.env.REACT_APP_PAYMENT_LIVE_MODE !== 'true' && (
        <Alert
          severity="info"
          icon={<span style={{ fontSize: '1.2rem' }}>🚀</span>}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            We're in Early Access Mode
          </Typography>
          <Typography variant="body2">
            JobMockAI is currently in pilot testing. Payments are in test mode — no real charges will occur.
            Want early access? Email us at{' '}
            <a href={`mailto:${INFO_EMAIL}`} style={{ color: 'inherit', fontWeight: 'bold' }}>
              {INFO_EMAIL}
            </a>{' '}
            and we'll set you up.
          </Typography>
        </Alert>
      )}

      {/* Razorpay test credentials helper */}
      {process.env.REACT_APP_PAYMENT_LIVE_MODE !== 'true' && selectedGateway === 'razorpay' && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }} icon={<span>🧪</span>}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Razorpay Test Mode — Use domestic Indian test cards only
          </Typography>
          <Typography variant="body2" component="div">
            <strong>✅ Domestic Visa:</strong> <code>4718 6014 2754 2407</code> &nbsp;|&nbsp; Expiry: <code>12/28</code> &nbsp;|&nbsp; CVV: <code>123</code>
            <br />
            <strong>✅ Domestic Mastercard:</strong> <code>5267 3181 8797 5449</code> &nbsp;|&nbsp; Expiry: <code>12/28</code> &nbsp;|&nbsp; CVV: <code>123</code>
            <br />
            <strong>❌ Avoid:</strong> <code>4111 1111 1111 1111</code> — treated as international, blocked
            <br />
            <strong>✅ Easiest — Test UPI:</strong> <code>success@razorpay</code> (always succeeds)
            <br />
            <strong>3DS OTP:</strong> Enter <code>1234</code> when prompted
          </Typography>
        </Alert>
      )}

      {/* Payment Gateway Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Choose Payment Gateway
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Recommended for your region: {selectedGateway?.toUpperCase()}
          </Typography>
          {selectedGateway === 'razorpay' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Razorpay charges in INR. Currency has been set to INR.
            </Alert>
          )}
          <Grid container spacing={2}>
            {paymentMethods.filter(method => method.currencies.includes(selectedCurrency)).map(method => (
              <Grid item xs={12} sm={6} key={method.gateway}>
                <Paper
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: selectedGateway === method.gateway ? 2 : 1,
                    borderColor: selectedGateway === method.gateway ? 'primary.main' : 'divider',
                    '&:hover': { borderColor: 'primary.main' }
                  }}
                  onClick={() => setSelectedGateway(method.gateway)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getGatewayIcon(method.gateway)}
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                        {method.gateway}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {method.description}
                      </Typography>
                    </Box>
                    {method.recommended && (
                      <Chip label="Recommended" color="primary" size="small" />
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Payment Terms + Auto-Renew Consent */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FlowAgreementsSection
            documents={paymentDocs}
            checkedDocs={paymentChecked}
            onCheckChange={onPaymentCheckChange}
            onViewDocument={setViewDoc}
            docsLoading={paymentDocsLoading}
            docsError={paymentDocsError}
            title="Payment terms"
            compact
          />
          <FormControlLabel
            sx={{ mt: 1.5, ml: 0 }}
            control={<Checkbox checked={autoRenewConsent} onChange={(e) => setAutoRenewConsent(e.target.checked)} />}
            label="I agree to auto-renew each billing cycle. I can cancel anytime."
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Required for subscriptions. Ensures compliant recurring billing consent.
          </Typography>
        </CardContent>
      </Card>

      {/* Payment Methods */}
      {selectedGateway === 'razorpay' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payment Method
            </Typography>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab icon={<CreditCard />} label="Card" />
              <Tab icon={<QrCode />} label="UPI" />
              <Tab icon={<AccountBalance />} label="Net Banking" />
              <Tab icon={<Payment />} label="Wallet" />
            </Tabs>

            {/* Tab Panels */}
            <Box sx={{ mt: 2 }}>
              {/* Card Tab Panel */}
              {activeTab === 0 && (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    Credit/Debit Card Payment
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your card details will be securely processed by Razorpay. We support all major credit and debit cards.
                  </Typography>
                </Box>
              )}

              {/* UPI Tab Panel */}
              {activeTab === 1 && (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    UPI Payment
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pay using any UPI app like PhonePe, Google Pay, Paytm, or BHIM. A QR code will be generated for easy payment.
                  </Typography>
                </Box>
              )}

              {/* Net Banking Tab Panel */}
              {activeTab === 2 && (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    Net Banking
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pay directly from your bank account using net banking. We support all major banks in India.
                  </Typography>
                </Box>
              )}

              {/* Wallet Tab Panel */}
              {activeTab === 3 && (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    Digital Wallet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pay using digital wallets like Paytm Wallet, Mobikwik, Freecharge, and other supported wallets.
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* PayPal Integration */}
      {selectedGateway === 'paypal' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              PayPal Payment
            </Typography>
            {process.env.REACT_APP_PAYPAL_CLIENT_ID ? (
              <PayPalScriptProvider 
                options={{ 
                  "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
                  currency: (() => {
                    console.log('PayPal SDK selectedCurrency:', selectedCurrency);
                    const validCurrency = getValidCurrencyCode(selectedCurrency);
                    console.log('PayPal SDK using currency:', validCurrency);
                    return validCurrency;
                  })()
                }}
                onLoadStart={() => console.log('PayPal SDK loading started')}
                onLoadEnd={() => console.log('PayPal SDK loading completed')}
                onError={(err) => {
                  console.error('PayPal SDK loading error:', err);
                  setError('Failed to load PayPal SDK. Please check your configuration.');
                }}
              >
                <PayPalButtons
                  style={{ layout: "vertical" }}
                  createOrder={async () => {
                    if (!paymentCheckoutReady) {
                      setError('Please accept payment terms and auto-renew consent to proceed.');
                      throw new Error('Consent required');
                    }
                    console.log('PayPal createOrder called - button clicked!');
                    try {
                      console.log('Creating payment intent...');
                      const intent = await createPaymentIntent();
                      console.log('Payment intent created:', intent);
                      if (!intent || !intent.id) {
                        // createPaymentIntent already called setError — don't overwrite it
                        throw new Error('Failed to create payment intent');
                      }
                      console.log('Returning PayPal order ID:', intent.id);
                      return intent.id;
                    } catch (error) {
                      console.error('PayPal createOrder error:', error);
                      // Only set a generic error if createPaymentIntent didn't already set one
                      // (it sets error via setError before returning null)
                      throw error;
                    }
                  }}
                  onApprove={async (data) => {
                    console.log('PayPal onApprove called:', data);
                    try {
                      setProcessing(true);
                      const response = await fetch(`${API_URL}/api/v1/billing/paypal/capture-order`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          order_id: data.orderID,
                          plan_slug: planSlugRef.current,
                        }),
                      });
                      const result = await response.json();
                      if (result.success) {
                        const planLabel = result.subscription?.plan_name || planName;
                        setSuccess(`Payment successful! Your ${planLabel} plan is now active.`);
                        if (onSuccess) onSuccess({ ...result, success: true });
                      } else {
                        setError(result.error || `Payment capture failed. Please contact ${BILLING_EMAIL}.`);
                      }
                    } catch (err) {
                      console.error('PayPal capture error:', err);
                      setError(`Payment capture failed. Please contact ${BILLING_EMAIL}.`);
                    } finally {
                      setProcessing(false);
                    }
                  }}
                  onError={(err) => {
                    console.error('PayPal error:', err);
                    // If createPaymentIntent already set a specific error (stored in ref), keep it
                    if (!paymentErrorRef.current) {
                      setError('PayPal payment failed. Please try again.');
                    }
                    paymentErrorRef.current = ''; // reset for next attempt
                  }}
                  onCancel={(data) => {
                    console.log('PayPal payment cancelled:', data);
                    setError('PayPal payment was cancelled');
                  }}
                  onClick={() => {
                    console.log('PayPal button clicked!');
                  }}
                />
              </PayPalScriptProvider>
            ) : (
              <Alert severity="error">
                PayPal client ID is not configured. Please check your environment variables.
                <br />
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  Debug: REACT_APP_PAYPAL_CLIENT_ID = {process.env.REACT_APP_PAYPAL_CLIENT_ID || 'undefined'}
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payment Button */}
      {selectedGateway === 'razorpay' && (
        <Card>
          <CardContent>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleRazorpayPayment}
              disabled={processing || !paymentCheckoutReady}
              startIcon={processing ? <CircularProgress size={20} /> : getPaymentMethodIcon(getSelectedPaymentMethod())}
              sx={{ py: 1.5 }}
            >
              {processing ? 'Processing...' : `Pay ${pricing ? formatCurrency(pricing.amount, selectedCurrency) : ''}`}
            </Button>
            
            {/* Security Notice */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
              <Shield fontSize="small" color="success" />
              <Typography variant="body2" color="text.secondary">
                Secured by 256-bit SSL encryption
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* UPI QR Code Dialog */}
      <Dialog open={showUpiQr} onClose={() => setShowUpiQr(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">UPI Payment</Typography>
            <Typography variant="body2" color="error">
              {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {upiData && (
            <Box sx={{ textAlign: 'center' }}>
              {/* QR Code */}
              <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                justifyContent: 'center',
                p: isMobileDevice() ? 1 : 2,
                bgcolor: isMobileDevice() ? 'transparent' : 'grey.50',
                borderRadius: isMobileDevice() ? 0 : 2,
                border: isMobileDevice() ? 'none' : '2px dashed #ccc'
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  {!isMobileDevice() && (
                    <Typography variant="body2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Scan with your mobile device
                    </Typography>
                  )}
                  <QRCodeComponent value={upiData.upi_id} size={isMobileDevice() ? 200 : 240} />
                </Box>
              </Box>
              
              {/* UPI ID */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" gutterBottom>
                  UPI ID:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                    {upiData.upi_id}
                  </Typography>
                  <IconButton size="small" onClick={copyUpiId}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Device-specific instructions */}
              {isMobileDevice() ? (
                <>
                  {/* Mobile: Show UPI Apps */}
                  <Typography variant="body2" gutterBottom>
                    Or pay using your UPI app:
                  </Typography>
                  <Grid container spacing={1} justifyContent="center">
                    {Object.entries(upiData.deep_links || {}).map(([app, link]) => (
                      <Grid item key={app}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => openUpiApp(app)}
                          startIcon={<Launch />}
                          sx={{ textTransform: 'capitalize' }}
                        >
                          {app}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </>
              ) : (
                <>
                  {/* Desktop: Show helpful instructions */}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Desktop Payment Instructions:</strong><br />
                      1. Scan the QR code with your mobile UPI app<br />
                      2. Or copy the UPI ID and use it in your mobile UPI app<br />
                      3. UPI app buttons work only on mobile devices
                    </Typography>
                  </Alert>
                  
                  <Typography variant="body2" gutterBottom color="text.secondary">
                    Available UPI apps: {Object.keys(upiData.deep_links || {}).join(', ')}
                  </Typography>
                </>
              )}

              {/* Progress */}
              <Box sx={{ mt: 3 }}>
                <LinearProgress variant="determinate" value={(300 - countdown) / 3} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Waiting for payment confirmation...
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUpiQr(false)}>Cancel</Button>
          <Button onClick={() => window.location.reload()} startIcon={<Refresh />}>
            Refresh
          </Button>
        </DialogActions>
      </Dialog>

      <ConsentModal
        open={!!viewDoc}
        document={viewDoc}
        onClose={() => setViewDoc(null)}
        onAccept={(doc) => {
          onPaymentCheckChange(doc.id, true);
          setViewDoc(null);
        }}
      />

      {/* Compliance Notice */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1, border: 1, borderColor: 'info.200' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Verified color="info" fontSize="small" />
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Compliance & Security
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          • PCI DSS Level 1 compliant payment processing<br/>
          • RBI guidelines compliant for Indian payments<br/>
          • GDPR compliant data handling<br/>
          • SOX compliant financial reporting
        </Typography>
      </Box>
    </Box>
  );
};

export default EnhancedPayment;